import { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { UstvaDeTab } from '../components/vat-reporting/UstvaDeTab';
import { VatEntriesTab } from '../components/vat-reporting/VatEntriesTab';
import { AnnualDeTab } from '../components/vat-reporting/AnnualDeTab';
import { SettingsTab } from '../components/vat-reporting/SettingsTab';
import { VatLangProvider, VatLangToggle, useVatLang } from '../components/vat-reporting/VatLang';
import type { VatCompany } from '../types/vatReporting';

/**
 * VAT reporting.
 *
 * One tab today - the monthly German package for one company. The shell is built for the tabs
 * that follow (Switzerland, quarterly, the annual reconciliation), which is why the company and
 * period selector sits here rather than inside the tab: every future tab needs the same three
 * choices, and having two of them disagree would be its own bug.
 */

type VatTab = 'ustva-de' | 'entries' | 'annual-de' | 'settings';

/**
 * `editOnly` is not a nicety. The settings tab writes the rules that decide what gets filed
 * with a tax authority; a viewer has no use for a form they cannot submit, and every one of
 * its endpoints refuses them anyway. The read-only view of the same rules already exists - it
 * is the derivation ladder in the UStVA tab. Same pattern as CashCockpitPage.
 */
const TABS: { key: VatTab; labelKey: string; editOnly?: boolean }[] = [
    { key: 'ustva-de', labelKey: 'tab.ustvaDe' },
    { key: 'entries', labelKey: 'tab.entries' },
    { key: 'annual-de', labelKey: 'tab.annualDe' },
    { key: 'settings', labelKey: 'tab.settings', editOnly: true },
];

/** Company names come out of the ERP and are the same in both languages. */
const COMPANIES: { code: VatCompany; label: string }[] = [
    { code: 'dch', label: 'Dolder AG (dch)' },
    { code: 'dbi', label: 'Dolder-Bigler AG (dbi)' },
    { code: 'bin', label: 'Retis Materials AG (bin)' },
];

/** Month names from the platform rather than a hard-coded list, so both languages are right. */
function monthNames(lang: string): string[] {
    const fmt = new Intl.DateTimeFormat(lang === 'de' ? 'de-CH' : 'en-GB', { month: 'long' });
    // Day 15 rather than 1: no month boundary to fall over, in any timezone.
    return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2026, i, 15)));
}

/** The Kennzahl map is seeded from 2024; before that every combination would be a finding. */
const FIRST_YEAR = 2024;

const STORAGE_KEY = 'vatReporting.selection';

interface Selection { company: VatCompany; year: number; month: number }

/**
 * The default is the PREVIOUS month, not the current one: a VAT return is filed for a month
 * that has closed, so the month somebody opens this page for is almost never today's.
 */
function defaultSelection(): Selection {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return { company: 'dch', year: prev.getFullYear(), month: prev.getMonth() + 1 };
}

function loadSelection(): Selection {
    if (typeof window === 'undefined') return defaultSelection();
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as Selection;
            if (COMPANIES.some(c => c.code === parsed.company)
                && parsed.year >= FIRST_YEAR && parsed.month >= 1 && parsed.month <= 12) {
                return parsed;
            }
        }
    } catch { /* a corrupt entry is not worth a crash */ }
    return defaultSelection();
}

export default function VatReportingPage() {
    // The provider wraps the whole page so the drill-down modals, which render through a portal
    // into document.body, still find the language.
    return (
        <VatLangProvider>
            <VatReportingPageInner />
        </VatLangProvider>
    );
}

function VatReportingPageInner() {
    const { t, lang } = useVatLang();
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/vat-reporting');
    const [activeTab, setActiveTab] = useState<VatTab>('ustva-de');
    const [selection, setSelection] = useState<Selection>(loadSelection);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selection)); }
        catch { /* private mode */ }
    }, [selection]);

    const onError = useCallback((message: string) => setError(message), []);

    const thisYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = thisYear; y >= FIRST_YEAR; y--) years.push(y);

    if (permLoading) {
        return <div className="p-8 text-center text-gray-500">{t('page.loading')}</div>;
    }
    if (!canView) {
        return (
            <div className="p-8">
                <h1 className="text-xl font-semibold text-gray-900">{t('page.title')}</h1>
                <p className="mt-2 text-gray-600">{t('page.noAccess')}</p>
            </div>
        );
    }

    const visibleTabs = TABS.filter(tab => !tab.editOnly || canEdit);

    return (
        <div className="p-4 space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">{t('page.title')}</h1>
                    <p className="text-sm text-gray-500">{t('page.subtitle')}</p>
                </div>
                <div className="flex items-end gap-2">
                    <label className="text-sm">
                        <span className="block text-xs text-gray-500 mb-1">{t('page.company')}</span>
                        <select value={selection.company}
                                onChange={e => setSelection(s => ({ ...s, company: e.target.value as VatCompany }))}
                                className="border rounded px-2 py-1.5 text-sm bg-white">
                            {COMPANIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                        </select>
                    </label>
                    {/* The annual tab is a year view - a month choice would suggest an influence
                        it does not have, so the selector disappears there rather than sitting
                        inert. The selection itself survives, for the switch back. */}
                    {activeTab !== 'annual-de' && (
                        <label className="text-sm">
                            <span className="block text-xs text-gray-500 mb-1">{t('page.month')}</span>
                            <select value={selection.month}
                                    onChange={e => setSelection(s => ({ ...s, month: Number(e.target.value) }))}
                                    className="border rounded px-2 py-1.5 text-sm bg-white">
                                {monthNames(lang).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                            </select>
                        </label>
                    )}
                    <label className="text-sm">
                        <span className="block text-xs text-gray-500 mb-1">{t('page.year')}</span>
                        <select value={selection.year}
                                onChange={e => setSelection(s => ({ ...s, year: Number(e.target.value) }))}
                                className="border rounded px-2 py-1.5 text-sm bg-white">
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </label>
                    <VatLangToggle />
                </div>
            </div>

            {error && (
                <div className="flex items-start justify-between gap-3 border border-red-200 bg-red-50 rounded p-3 text-sm text-red-800">
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 shrink-0">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            <div className="border-b">
                <nav className="flex -mb-px">
                    {visibleTabs.map(tab => (
                        <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === tab.key
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}>
                            {t(tab.labelKey)}
                        </button>
                    ))}
                </nav>
            </div>

            {activeTab === 'ustva-de' && (
                <UstvaDeTab key={`${selection.company}-${selection.year}-${selection.month}`}
                            company={selection.company}
                            year={selection.year}
                            month={selection.month}
                            canEdit={canEdit}
                            onError={onError} />
            )}

            {activeTab === 'entries' && (
                <VatEntriesTab key={`${selection.company}-${selection.year}-${selection.month}`}
                               company={selection.company}
                               year={selection.year}
                               month={selection.month}
                               canEdit={canEdit}
                               onError={onError} />
            )}

            {/* Keyed on company + year: the annual view has no month, and keeping the month out
                of the key means changing it (on another tab) does not remount this one. */}
            {activeTab === 'annual-de' && (
                <AnnualDeTab key={`${selection.company}-${selection.year}`}
                             company={selection.company}
                             year={selection.year}
                             canEdit={canEdit}
                             onError={onError} />
            )}

            {/* Keyed on the company only: the configuration is not per month, and remounting it
                every time somebody changes the month would throw away a filter and a scroll
                position for nothing. */}
            {activeTab === 'settings' && canEdit && (
                <SettingsTab key={selection.company}
                             company={selection.company}
                             canEdit={canEdit}
                             onError={onError} />
            )}
        </div>
    );
}
